import React, {Suspense, useEffect, useRef, useState} from 'react';
import {Spinner} from 'react-bootstrap';
import {Box, Tooltip} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ErrorBoundary from '../components/ErrorBoundary';
import {TableInvoiceComponentProps} from '../models/table-models';
import {ErrorComponent} from '../components/ErrorComponent';
import {useAsyncResource} from 'use-async-resource';
import {useGetAllTurnovers, useGetUsers} from '../utils/users-hooks';
import {Turnover} from '../models/user-models';
import {InvoiceView} from './InvoiceView';
import {TurnoversInner} from './turnover/TurnoversInner';
import {useGetConfigInvoiceDetails} from "../utils/configuration-hooks";
import dateFormat from "dateformat";
import {date} from "../utils/Enums/Common";
import {turnoverTableHeaders} from "../utils/Enums/TableHeaders";
import {ExportTurnovers} from "../exports/ExportTurnovers";
import OpeningBalances from './OpeningBalances';

export const Turnovers = ({filter, pagination, refetchEntitiesHook}: TableInvoiceComponentProps & {
    refetchEntitiesHook: () => Promise<void>,
}) => {
    const {t, i18n} = useTranslation('translation', {keyPrefix: 'common'});
    const {t: tableT} = useTranslation('translation', {keyPrefix: 'table'});
    const {t: openingT} = useTranslation('translation', {keyPrefix: 'openingBalances'});
    const amount = (value: number) => Number(String(value).replace(/,/g, ''));
    const money = (value: number) => amount(value).toLocaleString(i18n.resolvedLanguage || i18n.language, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

    const [getTurnovers, fetchNewTurnovers] = useAsyncResource(useGetAllTurnovers, pagination, {
        ...filter,
    });
    const turnoverDTO = getTurnovers();
    const totalSum = amount(turnoverDTO?.sum ?? 0) + amount(turnoverDTO?.unpaidBankSum ?? 0);

    const [getInvoiceDetails] = useAsyncResource(useGetConfigInvoiceDetails, []);
    const invoiceDetails = getInvoiceDetails();

    const turnoversRef = useRef<HTMLDivElement>(null);
    const totalRef = useRef<HTMLDivElement>(null);
    const summaryRef = useRef<HTMLDivElement>(null);
    const [summaryHeight, setSummaryHeight] = useState<number>(240);
    const [distributorsExpanded, setDistributorsExpanded] = useState(false);

    useEffect(() => {
        const summary = summaryRef.current;
        if (!summary) return;
        const updateHeight = () => setSummaryHeight(summary.getBoundingClientRect().height);
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(summary);
        return () => observer.disconnect();
    }, []);

    const [getAllUsers] = useAsyncResource(useGetUsers, {});
    const {items: users} = getAllUsers();
    const [selectedItem, setSelectedItem] = useState<Turnover>();

    const refreshTurnovers = async () => {
        // Clearing the shared cache does not replace this component's reader.
        // Refresh it explicitly after both table actions and invoice-dialog changes.
        fetchNewTurnovers(pagination, filter);
        await refetchEntitiesHook();
    };

    useEffect(() => {
        if(selectedItem!==undefined) {
            // И по задължението, не само по id на оборота: неплатените редове идват без id и търсенето
            // само по него хващаше първия от тях, тоест диалогът можеше да смени фактурата под ръцете.
            setSelectedItem(()=>turnoverDTO.turnoverList.find(
                ({id, invoice}) => id === selectedItem.id && invoice.id === selectedItem.invoice.id));
        }
    }, [turnoverDTO]);

    return (
        <>
            {selectedItem?.invoice.openingBalanceId ? (
                <OpeningBalances
                    balanceId={selectedItem.invoice.openingBalanceId}
                    onClose={() => setSelectedItem(undefined)}
                    onChanged={refreshTurnovers}
                />
            ) : (
                <InvoiceView
                    title={t('Details for')}
                    item={selectedItem?.invoice}
                    turnover={selectedItem}
                    setSelectedItem={() => setSelectedItem(undefined)}
                    refetchEntitiesHook={refreshTurnovers}
                />
            )}
            <ErrorBoundary fallback={<ErrorComponent errorText={t('invoices')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <div className='mb-3 p-3 d-flex flex-column flex-lg-row align-items-start gap-3 border-primary w-100'>
                        <Box ref={summaryRef} className='d-flex flex-column gap-2 flex-shrink-0'
                             sx={{ '& > p': { fontSize: '16px' } }}>
                            <p className='h5 m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('totalSum')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(totalSum)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('paidIncluding')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.sum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 m-0 d-flex justify-content-between gap-4'>
                                <Tooltip arrow title={
                                    <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 2, rowGap: 0.5 }}>
                                        <span>{tableT('cashPayments')}:</span>
                                        <Box component='span' sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                            {money(turnoverDTO?.paidCashSum ?? 0)} {invoiceDetails?.currency}
                                        </Box>
                                        <span>{tableT('cardPaymentsInline')}:</span>
                                        <Box component='span' sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                            {money(turnoverDTO?.paidCardSum ?? 0)} {invoiceDetails?.currency}
                                        </Box>
                                    </Box>
                                }>
                                    <span tabIndex={0} style={{ fontSize: 'inherit', paddingLeft: '16px', cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '4px' }}>
                                        {tableT('paidFiscalSubrow')}:
                                    </span>
                                </Tooltip>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(
                                        amount(turnoverDTO?.paidCashSum ?? 0) + amount(turnoverDTO?.paidCardSum ?? 0)
                                    )}{' '}
                                    {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit', paddingLeft: '16px' }}>{tableT('paidBankSubrow')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.paidBankSum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 text-danger m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('unpaidBankPeriodSum')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.unpaidBankSum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('paidOpeningSum')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.paidOpeningSum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 m-0 pt-3 border-top d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('paidBankCurrentSum')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.paidBankCurrentSum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                            <p className='h5 text-danger m-0 d-flex justify-content-between gap-4'>
                                <span style={{ fontSize: 'inherit' }}>{tableT('unpaidBankCurrentSum')}:</span>
                                <span style={{ fontSize: 'inherit' }} className='text-end text-nowrap'>
                                    {money(turnoverDTO?.unpaidBankCurrentSum ?? 0)} {invoiceDetails?.currency}
                                </span>
                            </p>
                        </Box>
                        {turnoverDTO?.turnovers?.length > 0 ? (
                            <div
                                className='mw-100 d-flex flex-column flex-grow-1'
                                style={{ minWidth: 0, maxHeight: distributorsExpanded ? undefined : summaryHeight }}
                            >
                                <div className='d-flex justify-content-between align-items-center gap-3 mb-2 flex-shrink-0'>
                                    <h2 className='mb-0' style={{ fontSize: '20px' }}>
                                        {t('turnoverByUsers')} ({invoiceDetails?.currency})
                                    </h2>
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-outline-primary text-nowrap'
                                        style={{ width: 70 }}
                                        aria-expanded={distributorsExpanded}
                                        aria-controls='distributor-turnover-table'
                                        onClick={() => setDistributorsExpanded((expanded) => !expanded)}
                                    >
                                        {tableT(distributorsExpanded ? 'collapseDistributors' : 'expandDistributors')}
                                    </button>
                                </div>
                                <div
                                    id='distributor-turnover-table'
                                    className='table-responsive border rounded'
                                    tabIndex={0}
                                    role='region'
                                    aria-label={t('turnoverByUsers')}
                                    style={{ minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}
                                >
                                    <table
                                        className='table table-striped table-hover align-middle mb-0'
                                        style={{
                                            fontVariantNumeric: 'tabular-nums',
                                            borderCollapse: 'separate',
                                            borderSpacing: 0,
                                        }}
                                    >
                                        <thead
                                            className='table-light'
                                            style={{ position: 'sticky', top: 0, zIndex: 1 }}
                                        >
                                            <tr>
                                                <th scope='col' className='px-3 py-1'>
                                                    {tableT('distributorName')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end text-nowrap'>
                                                    {tableT('cashPayments')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end text-nowrap'>
                                                    {tableT('cardPayments')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end text-nowrap'>
                                                    {tableT('cashAndCardPayments')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end text-nowrap'>
                                                    {tableT('bankPayments')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end border-start'>
                                                    {openingT('currentDocuments')}
                                                </th>
                                                <th scope='col' className='px-3 py-1 text-end'>
                                                    {openingT('openingReceipts')}
                                                </th>
                                                <th
                                                    scope='col'
                                                    className='px-3 py-1 text-end border-start table-primary'
                                                >
                                                    {tableT('totalPaid')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {turnoverDTO.turnovers.map(
                                                ({
                                                    userId,
                                                    sum,
                                                    currentSum,
                                                    openingSum,
                                                    paidCashSum,
                                                    paidCardSum,
                                                    paidBankSum,
                                                }) => {
                                                    return (
                                                        <tr key={userId ?? 'unassigned'}>
                                                            <td className='px-3 py-1 fw-bold'>
                                                                {userId == null
                                                                    ? t('unassignedTurnover')
                                                                    : users.find((user) => user.id === userId)?.name ??
                                                                      '—'}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap'>
                                                                {money(paidCashSum)}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap'>
                                                                {money(paidCardSum)}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap fw-bold'>
                                                                {money(amount(paidCashSum) + amount(paidCardSum))}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap'>
                                                                {money(paidBankSum)}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap border-start'>
                                                                {money(currentSum)}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap'>
                                                                {money(openingSum)}
                                                            </td>
                                                            <td className='px-3 py-1 text-end text-nowrap border-start table-primary fw-bold'>
                                                                {money(sum)}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                        <tfoot
                                            className='table-light border-top border-2 fw-bold'
                                            style={{
                                                position: 'sticky',
                                                bottom: 0,
                                                zIndex: 1,
                                                backgroundColor: 'var(--bs-table-bg)',
                                                boxShadow: '0 2px 0 var(--bs-table-bg)',
                                            }}
                                        >
                                            <tr>
                                                <th scope='row' className='px-3 py-0'>
                                                    {tableT('totalSum')}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.paidCashSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.paidCardSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.paidCashSum) + amount(r.paidCardSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.paidBankSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap border-start'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.currentSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap'>
                                                    {money(
                                                        turnoverDTO.turnovers.reduce(
                                                            (s, r) => s + amount(r.openingSum),
                                                            0
                                                        )
                                                    )}
                                                </th>
                                                <th className='px-3 py-1 text-end text-nowrap border-start table-primary'>
                                                    {money(turnoverDTO.sum)}
                                                </th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className='d-flex flex-column align-items-center flex-shrink-0 ms-lg-auto'>
                            <h5 className='title mb-1'>{t('documents')}</h5>
                            <Suspense fallback={<Spinner animation='border' />}>
                                <ExportTurnovers
                                    pagination={pagination}
                                    turnoversFilter={filter}
                                    fileName={t('turnovers') + '-' + dateFormat(new Date(), date)}
                                    wscols={turnoverTableHeaders}
                                    users={users}
                                />
                            </Suspense>
                        </div>
                    </div>

                    <h3 className='mb-3'>{t('payments')}</h3>
                    <TurnoversInner
                        pagination={pagination}
                        items={turnoverDTO?.turnoverList ?? []}
                        setSelectedItem={setSelectedItem}
                        users={users}
                        pager={turnoverDTO?.pager}
                        refetchEntitiesHook={refreshTurnovers}
                    />
                    <div ref={totalRef} className='mb-5 w-100'>
                        <Suspense fallback={<Spinner animation='border' />}>
                            {/*<TurnoversGraph userIds={usersFilter} users={users} filter={filter} />*/}
                        </Suspense>
                    </div>
                    <div ref={turnoversRef} className='my-5 w-100 h-100'>
                        <Suspense fallback={<Spinner animation='border' />}>
                            {/*<MonthlyReportGraph {...{ turnoversFilter }} />*/}
                        </Suspense>
                    </div>
                </Suspense>
            </ErrorBoundary>
        </>
    );
};
